import { Component, Input } from '@angular/core';
import { LoadingController, ModalController, PopoverController, AlertController } from '@ionic/angular';

import { SlackChannel } from 'src/models';
import { MessageService } from 'src/services/message.service';
import { SlackService } from 'src/services/slack.service';

import { BaseChannelSelectorModal } from './base-channel-selector-modal.directive';

@Component({
  selector: 'app-channel-selector-modal',
  templateUrl: './channel-selector-modal.component.html',
  styleUrls: ['./channel-selector-modal.component.css'],
})
export class ChannelSelectorModalComponent extends BaseChannelSelectorModal {
  @Input() public selectedChannel?: SlackChannel;

  public selectedChannelId?: string;
  public channels: SlackChannel[] = [];
  public connectedTeams: any[];
  public selectedTeam: any;
  public filteredChannels: SlackChannel[];
  public userConnectedToSlack = false;
  public teamConnectedToSlack = false;
  public isLoading: boolean;
  public isLoadingChannels: boolean;
  public isOpen = true;
  public isConnecting = false;
  public searchText: string;
  public filterType: 'all' | 'shared' | 'internal' = 'all';

  constructor(
    protected override modalCtrl: ModalController,
    protected override msgService: MessageService,
    protected override slackService: SlackService,
    protected override popoverCtrl: PopoverController,
    protected override loadingCtrl: LoadingController,
    private alertController: AlertController,
  ) {
    super(modalCtrl, msgService, slackService, popoverCtrl, loadingCtrl);
  }

  initializeSelectionState(): void {
    this.selectedChannelId = this.selectedChannel?.id;
  }

  shouldDefaultForceInternalFilter(): boolean {
    return !this.selectedChannel || !this.selectedChannel.isShared;
  }

  isChannelSelected(channel: SlackChannel): boolean {
    return this.selectedChannelId === channel.id;
  }

  async handleChannelInteraction(channel: SlackChannel): Promise<void> {
    this.selectedChannelId = channel.id;
    const channelToSelect = channel;

    if (channelToSelect?.id) {
      const loader = await this.loadingCtrl.create({
        message: 'Connecting...',
        spinner: 'crescent',
      });
      await loader.present();
      try {
        await this.slackService.connectChannel(channelToSelect.id, this.selectedTeam.connectionId);
        await loader.dismiss();
        this.isOpen = false;
        this.modalCtrl.dismiss({ channel: channelToSelect });
      } catch (error) {
        console.error('Failed to connect channel:', error);
        this.msgService.showError('Failed to connect the selected channel.');
        await loader.dismiss();
      }
    } else {
      this.isOpen = false;
      this.modalCtrl.dismiss();
    }
  }

  confirmSelection(): void {
    const resultChannel = this.channels?.find((c) => c.id === this.selectedChannelId);
    this.isOpen = false;
    this.modalCtrl.dismiss({ channel: resultChannel });
  }

  async promptAddChannelById() {
    if (!this.selectedTeam || !this.selectedTeam.connectionId) {
      this.msgService.showError('Please select a Slack team first.');
      return;
    }

    const alert = await this.alertController.create({
      header: 'Add Channel by ID',
      inputs: [
        {
          name: 'channelId',
          type: 'text',
          placeholder: 'Slack Channel ID (e.g. C123XYZ)',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Add',
          handler: (data) => {
            if (data.channelId && data.channelId.trim()) {
              this.addChannelManually(data.channelId.trim());
            } else {
              this.msgService.show('Please enter a channel ID.');
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async addChannelManually(channelId: string) {
    if (!this.selectedTeam || !this.selectedTeam.connectionId) {
      this.msgService.showError('No Slack team selected or connection ID missing.');
      return;
    }

    this.isLoading = true;

    try {
      const fetchedChannel = await this.slackService.getChannel(
        channelId,
        this.selectedTeam.connectionId,
        this.selectedTeam.id,
      );

      if (fetchedChannel) {
        this.isLoading = false;

        await this.handleChannelInteraction(fetchedChannel);
      } else {
        this.msgService.showError(
          `Could not find channel with ID: ${channelId}. Please ensure the ID is correct, the bot has access to it, and it belongs to the selected team.`,
        );
        this.isLoading = false;
      }
    } catch (error) {
      console.error('Error adding channel manually to modal:', error);

      let message = 'Failed to add channel.';

      if (error.message) {
        message = error.message;
      } else if (typeof error === 'string') {
        message = error;
      }

      this.msgService.showError(message);
      this.isLoading = false;
    }
  }
}
